import { useEffect, useRef, useState } from 'react';
import type { PublicForm } from '../types';
import { Skeleton } from '../components/primitives';

type EmbedState = 'loading' | 'loaded' | 'completed' | 'error';

const PANDADOC_ORIGIN = /^https:\/\/([a-z0-9-]+\.)?pandadoc\.com$/;

/**
 * Renders a PandaDoc form via the official JS embed contract:
 * an iframe pointed at the form's share URL, with `embed.form.*` postMessage
 * events (loaded / started / completed / exception) driving the UI states.
 */
export function PandaDocEmbed({ form }: { form: PublicForm }) {
  const [state, setState] = useState<EmbedState>('loading');
  const containerRef = useRef<HTMLDivElement>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    setState('loading');
    const iframe = document.createElement('iframe');
    iframe.src = form.embedUrl;
    iframe.title = form.displayName;
    iframe.loading = 'lazy';
    iframe.className = 'h-full w-full border-0 bg-white';
    node.replaceChildren(iframe);

    const onMessage = (e: MessageEvent) => {
      if (!PANDADOC_ORIGIN.test(e.origin) || e.source !== iframe.contentWindow) return;
      try {
        const message = JSON.parse(String(e.data)) as { event?: string };
        const event = String(message.event ?? '').replace('embed.form.', '');
        if (event === 'loaded') setState('loaded');
        else if (event === 'completed') setState('completed');
        else if (event === 'exception') setState('error');
      } catch {
        // not a PandaDoc message — ignore
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      iframe.remove();
    };
  }, [form.embedUrl, form.displayName, reloadKey]);

  // Near-full viewport: header (~66px) + top padding + fallback link ≈ 110px.
  // dvh handles mobile browser chrome; config.height stays the per-form override.
  const height = `min(${form.config?.height ?? 1100}px, calc(100dvh - 110px))`;

  if (state === 'completed') {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-ok/30 bg-ok/5 px-6 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ok/15 text-ok">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <h2 className="mt-5 text-xl font-semibold">Form submitted</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted">
          Thank you — your submission for <strong className="text-foreground">{form.displayName}</strong> is
          complete. You'll receive a copy and next steps by email from PandaDoc.
        </p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-6 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input"
        >
          Start a new submission
        </button>
      </div>
    );
  }

  return (
    <div>
      {state === 'error' && (
        <div role="alert" className="mb-4 flex flex-col gap-3 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span>
            The form hit an error while loading. You can retry below, or{' '}
            <a className="font-medium underline underline-offset-2" href={form.embedUrl} target="_blank" rel="noreferrer">
              open it in a new tab
            </a>
            .
          </span>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="shrink-0 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium transition-colors hover:bg-input"
          >
            Retry
          </button>
        </div>
      )}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card" style={{ height }}>
        {state === 'loading' && (
          <div className="absolute inset-0 z-10 flex flex-col gap-4 p-6" aria-hidden>
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="mt-4 h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        )}
        <div ref={containerRef} className="h-full w-full" />
      </div>
      <p className="mt-3 text-center text-xs text-muted">
        Trouble viewing the form?{' '}
        <a className="underline underline-offset-2 transition-colors hover:text-foreground" href={form.embedUrl} target="_blank" rel="noreferrer">
          Open it in a new tab
        </a>
      </p>
    </div>
  );
}
